import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Upload, Sparkles, SlidersHorizontal, Check, Plus, Trash2, Send, Loader2,
  FileText, Video, Image as ImageIcon, Music, Wand2, X, Clock, Lock, Users, ArrowUp, ArrowDown, Eye, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fileUrl } from "../lib/api";
import { MoveToVaultButton } from "./MoveToVault";

const OPS = [
  { v: ">=", l: "at least" }, { v: ">", l: "more than" }, { v: "==", l: "exactly" },
  { v: "<=", l: "at most" }, { v: "<", l: "under" },
];
const ROLES = ["admin", "organizer", "user"];
const kindIcon = (k) => ({ video: Video, image: ImageIcon, audio: Music, pdf: FileText }[k] || FileText);

// How a multi-recipient plan releases. Maps 1:1 to backend per_recipient values.
const GROUP_MODES = [
  { v: "individual", l: "To each individually" },
  { v: "together", l: "Everyone together" },
  { v: "any", l: "Any one of them" },
  { v: "count", l: "At least N of them", threshold: "count" },
  { v: "percent", l: "At least X% of them", threshold: "percent" },
  { v: "majority", l: "The majority of them" },
  { v: "youngest", l: "The youngest recipient" },
  { v: "oldest", l: "The oldest recipient" },
];

async function uploadFile(f) {
  const fd = new FormData();
  fd.append("file", f);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  const ct = data.content_type || "";
  const kind = ct.startsWith("video") ? "video" : ct.startsWith("image") ? "image" : ct.startsWith("audio") ? "audio" : "pdf";
  return { path: data.path, kind, title: f.name, content_type: ct };
}

function MediaDrop({ onUploaded, compact }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const checkVideoDuration = (file) => new Promise((resolve) => {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => { URL.revokeObjectURL(v.src); resolve(v.duration); };
    v.onerror = () => resolve(0);
    v.src = URL.createObjectURL(file);
  });
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type.startsWith("video/")) {
      const dur = await checkVideoDuration(f);
      if (dur > 61) { toast.error("Videos must be 1 minute or shorter. Please trim it and try again."); e.target.value = ""; return; }
    }
    setBusy(true);
    try { onUploaded(await uploadFile(f)); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <>
      <input ref={inputRef} type="file" accept="image/*,video/*,audio/*,.pdf" className="hidden" onChange={pick} data-testid="sv-media-input" />
      <button onClick={() => inputRef.current?.click()} disabled={busy} data-testid="sv-media-upload-btn"
        className={`w-full rounded-xl flex items-center gap-3 ${compact ? "p-3" : "p-4"}`} style={{ background: "var(--overlay)", border: "1px dashed var(--border)" }}>
        <div className="h-10 w-10 rounded-lg grid place-items-center shrink-0" style={{ background: "color-mix(in srgb, var(--accent) 14%, transparent)" }}>
          {busy ? <Loader2 size={18} className="animate-spin gold-text" /> : <Upload size={18} className="gold-text" />}
        </div>
        <div className="text-left">
          <div className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>Attach media</div>
          <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>Photo, video, audio or PDF</div>
        </div>
      </button>
    </>
  );
}

function MediaBadge({ media, onClear }) {
  const Icon = kindIcon(media.kind);
  return (
    <div className="flex items-center gap-2 rounded-xl px-3 py-2 mb-3" style={{ background: "color-mix(in srgb, var(--accent) 10%, transparent)", border: "1px solid color-mix(in srgb, var(--accent) 40%, transparent)" }} data-testid="sv-media-badge">
      <Icon size={16} className="gold-text" />
      <span className="text-xs flex-1 truncate" style={{ color: "var(--text-primary)" }}>{media.title}</span>
      {onClear && <button onClick={onClear} data-testid="sv-media-clear"><X size={15} style={{ color: "var(--text-muted)" }} /></button>}
    </div>
  );
}

// Recipients: a dropdown of every family member; picking one adds a card with a checkmark.
function RecipientSelect({ recipients, selected, setSelected }) {
  const remaining = recipients.filter((r) => !selected.includes(r.user_id));
  return (
    <div data-testid="sv-recipient-select">
      <select value="" onChange={(e) => e.target.value && setSelected((s) => [...s, e.target.value])} data-testid="sv-recipient-dropdown"
        className="w-full rounded-xl px-3 py-3 text-sm" style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)" }}>
        <option value="">{remaining.length ? "Add a recipient…" : "All members added"}</option>
        {remaining.map((r) => <option key={r.user_id} value={r.user_id}>{r.name}{r.age != null ? ` (${r.age})` : ""}</option>)}
      </select>
      <div className="space-y-2 mt-2">
        {selected.map((id) => {
          const r = recipients.find((x) => x.user_id === id);
          if (!r) return null;
          return (
            <div key={id} className="flex items-center gap-2 rounded-xl px-3 py-2.5" style={{ background: "var(--overlay)", border: "1px solid color-mix(in srgb, var(--accent) 45%, transparent)" }} data-testid={`sv-recipient-card-${id}`}>
              <span className="h-5 w-5 rounded-full grid place-items-center shrink-0" style={{ background: "var(--accent)" }}><Check size={13} color="var(--bg-base)" /></span>
              <span className="text-sm font-medium flex-1" style={{ color: "var(--text-primary)" }}>{r.name}</span>
              <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>{r.role}{r.age != null ? ` · ${r.age}y` : ""}</span>
              <button onClick={() => setSelected((s) => s.filter((x) => x !== id))} data-testid={`sv-recipient-remove-${id}`}><X size={15} style={{ color: "var(--text-muted)" }} /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Two cascading filters: pick a class, then a condition within it. Some conditions need a
// specific target (a person / reunion / prior vault item) — those render a picker dropdown.
function ConditionRow({ cond, triggers, pickers, onChange, onRemove }) {
  const categories = useMemo(() => [...new Set(triggers.map((t) => t.category))], [triggers]);
  const meta = triggers.find((t) => t.metric === cond.metric) || {};
  const category = meta.category || categories[0];
  const inCat = triggers.filter((t) => t.category === category);
  const vt = meta.value_type;
  const isDate = vt === "datetime";
  const isRole = vt === "role";
  const isBool = vt === "bool";
  const isPerson = vt === "person";
  const isReunion = vt === "reunion";
  const isItem = vt === "item";
  const isPicker = isPerson || isReunion || isItem;
  const isPeriod = meta.unit === "period";
  const selStyle = { background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)" };

  const pickerList = isPerson ? (pickers?.people || []) : isReunion ? (pickers?.reunions || []) : (pickers?.items || []);
  const pickerLabel = (o) => o.name || o.title || "—";
  const pickerEmpty = isPerson ? "No other members yet" : isReunion ? "No reunions yet" : "No prior Vault items yet";

  const pickCategory = (cat) => {
    const first = triggers.find((t) => t.category === cat);
    onChange({ metric: first.metric, op: ">=", value: "", unit: first.unit === "period" ? "years" : undefined });
  };

  return (
    <div className="rounded-xl p-3 space-y-2" style={{ background: "var(--overlay)", border: "1px solid var(--border)" }} data-testid="sv-condition-row">
      <div className="flex items-start gap-2">
        <div className="flex-1 grid grid-cols-2 gap-2">
          <select value={category} onChange={(e) => pickCategory(e.target.value)} data-testid="sv-condition-category" className="rounded-lg px-2 py-2 text-sm" style={selStyle}>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={cond.metric} onChange={(e) => { const t = triggers.find((x) => x.metric === e.target.value); onChange({ metric: e.target.value, op: ">=", value: "", unit: t.unit === "period" ? "years" : undefined }); }} data-testid="sv-condition-metric" className="rounded-lg px-2 py-2 text-sm" style={selStyle}>
            {inCat.map((t) => <option key={t.metric} value={t.metric}>{t.label}</option>)}
          </select>
        </div>
        <button onClick={onRemove} data-testid="sv-condition-remove" className="p-2 rounded-lg" style={{ color: "var(--text-muted)" }}><Trash2 size={16} /></button>
      </div>

      {isPicker ? (
        <select value={cond.value} onChange={(e) => onChange({ ...cond, value: e.target.value })} data-testid="sv-condition-picker" className="w-full rounded-lg px-2 py-2 text-sm" style={selStyle}>
          <option value="">{pickerList.length ? "Choose…" : pickerEmpty}</option>
          {pickerList.map((o) => <option key={o.id} value={o.id}>{pickerLabel(o)}</option>)}
        </select>
      ) : !isBool && (
        <div className="flex items-center gap-2">
          {!isDate && !isRole && (
            <select value={cond.op} onChange={(e) => onChange({ ...cond, op: e.target.value })} data-testid="sv-condition-op" className="rounded-lg px-2 py-2 text-sm" style={selStyle}>
              {OPS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          )}
          {isDate ? (
            <input type="datetime-local" value={cond.value} onChange={(e) => onChange({ ...cond, value: e.target.value })} data-testid="sv-condition-value" className="flex-1 rounded-lg px-2 py-2 text-sm" style={selStyle} />
          ) : isRole ? (
            <select value={cond.value} onChange={(e) => onChange({ ...cond, value: e.target.value })} data-testid="sv-condition-value" className="flex-1 rounded-lg px-2 py-2 text-sm" style={selStyle}>
              <option value="">Select role…</option>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          ) : (
            <input type="number" min="0" value={cond.value} onChange={(e) => onChange({ ...cond, value: e.target.value })} placeholder="value" data-testid="sv-condition-value" className="w-24 rounded-lg px-2 py-2 text-sm" style={selStyle} />
          )}
          {isPeriod && (
            <select value={cond.unit || "years"} onChange={(e) => onChange({ ...cond, unit: e.target.value })} data-testid="sv-condition-unit" className="rounded-lg px-2 py-2 text-sm" style={selStyle}>
              {["days", "weeks", "months", "years"].map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          )}
        </div>
      )}
      {(cond.metric === "sender_age" || cond.metric === "recipient_age") && (
        <div className="flex items-center gap-2 text-xs" style={{ color: "var(--text-secondary)" }} data-testid="sv-age-offset">
          <input type="number" min="0" value={cond.offset?.n ?? ""} onChange={(e) => onChange({ ...cond, offset: e.target.value ? { n: Number(e.target.value), unit: cond.offset?.unit || "days", dir: cond.offset?.dir || "before" } : undefined })} placeholder="0" className="w-14 rounded-lg px-2 py-1.5" style={selStyle} data-testid="sv-offset-n" />
          <select value={cond.offset?.unit || "days"} onChange={(e) => onChange({ ...cond, offset: { ...(cond.offset || { n: 0 }), unit: e.target.value } })} className="rounded-lg px-1.5 py-1.5" style={selStyle} data-testid="sv-offset-unit">
            {["days", "weeks", "months", "years"].map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
          <select value={cond.offset?.dir || "before"} onChange={(e) => onChange({ ...cond, offset: { ...(cond.offset || { n: 0 }), dir: e.target.value } })} className="rounded-lg px-1.5 py-1.5" style={selStyle} data-testid="sv-offset-dir">
            <option value="before">before</option>
            <option value="after">after</option>
          </select>
          <span>the birthday (optional)</span>
        </div>
      )}
    </div>
  );
}

// One-tap preview of exactly what a recipient will see in their Inbox when the item unlocks.
function DeliveryPreview({ media, title, note, per_recipient, group_threshold, recipients, rule, onClose, actions, heading = "Recipient preview" }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post("/secret-vault/preview", { rule, recipients, per_recipient, group_threshold });
        setData(data);
      } catch (e) { setErr(formatApiError(e.response?.data?.detail) || "Couldn't build preview"); }
    })();
  }, []); // eslint-disable-line
  const url = media?.path ? fileUrl(media.path) : null;
  const kind = media?.kind;
  const Card = ({ toName }) => (
    <div className="rounded-2xl p-4 mb-3" style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)" }} data-testid="sv-preview-card">
      <div className="flex items-center gap-2 gold-text mb-1"><Lock size={14} /><span className="text-[10px] uppercase tracking-widest">From the Secret Vault</span></div>
      <div className="font-serif-display text-xl" style={{ color: "var(--text-primary)" }}>{title || media?.title || "Untitled"}</div>
      <div className="text-xs mb-2" style={{ color: "var(--text-muted)" }}>{toName ? `To ${toName}` : "To your recipients"} · from {data?.from_name || "you"}</div>
      {(note) && <div className="rounded-lg p-2.5 mb-2 text-sm italic" style={{ background: "color-mix(in srgb, var(--accent) 10%, transparent)", color: "var(--text-primary)" }}>“{note}”</div>}
      {url && (
        <div className="rounded-lg overflow-hidden mb-2" style={{ background: "#000" }}>
          {kind === "video" ? <video src={url} controls className="w-full" />
            : kind === "image" ? <img src={url} alt="" className="w-full" />
            : kind === "audio" ? <audio src={url} controls className="w-full p-2" />
            : <div className="flex items-center gap-2 p-3 text-sm gold-text"><FileText size={16} /> {media?.title || "Document"}</div>}
        </div>
      )}
      {media?.body_md && <div className="vault-md rounded-lg p-2.5 mb-2 text-sm" style={{ background: "var(--overlay)", color: "var(--text-primary)" }}><ReactMarkdown remarkPlugins={[remarkGfm]}>{media.body_md}</ReactMarkdown></div>}
      <div className="rounded-lg p-2.5" style={{ background: "var(--overlay)" }}>
        <div className="text-[11px] uppercase tracking-wide mb-1" style={{ color: "var(--text-muted)" }}>Conditions that unlock this</div>
        <div className="text-sm" style={{ color: "var(--text-primary)" }}>{data?.summary || "—"}</div>
      </div>
    </div>
  );
  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center" style={{ background: "rgba(0,0,0,0.65)" }} onClick={onClose} data-testid="sv-preview-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl p-5 max-h-[90vh] overflow-y-auto no-scrollbar" style={{ background: "var(--bg-surface)", border: "1px solid var(--border)" }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><Eye size={16} className="gold-text" /><span className="font-serif-display text-lg">{heading}</span></div>
          <button onClick={onClose} data-testid="sv-preview-close"><X size={20} style={{ color: "var(--text-secondary)" }} /></button>
        </div>
        {err ? <div className="text-sm py-8 text-center" style={{ color: "var(--text-muted)" }}>{err}</div>
          : !data ? <div className="grid place-items-center py-10"><Loader2 className="animate-spin gold-text" /></div>
          : (
            <>
              {data.group_text && <div className="text-xs mb-3 rounded-lg p-2.5" style={{ background: "color-mix(in srgb, var(--accent) 10%, transparent)", color: "var(--text-secondary)" }} data-testid="sv-preview-grouptext">{data.group_text}</div>}
              {data.individual && data.recipients.length
                ? data.recipients.map((r) => <Card key={r.id} toName={r.name} />)
                : <Card toName={data.recipients.map((r) => r.name).join(", ")} />}
              {actions}
            </>
          )}
      </div>
    </div>, document.body);
}

function ProposalCard({ proposal, media, onAccept, onNew, onPreview, busy }) {
  return (
    <div className="rounded-2xl p-4 mt-1" style={{ background: "var(--bg-elevated)", border: "1px solid color-mix(in srgb, var(--accent) 40%, transparent)" }} data-testid="sv-proposal-card">
      <div className="flex items-center gap-2 mb-2"><Wand2 size={17} className="gold-text" /><span className="font-serif-display text-xl">{proposal.title}</span></div>
      <div className="text-sm mb-3" style={{ color: "var(--text-primary)" }}>{proposal.rationale}</div>
      <div className="rounded-lg p-3 mb-2" style={{ background: "var(--overlay)" }}>
        <div className="text-[11px] uppercase tracking-wide mb-1" style={{ color: "var(--text-muted)" }}>Release when</div>
        <div className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>{proposal.summary}</div>
        <div className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>{proposal.per_recipient === "together" ? "To everyone together" : "Individually to each recipient"}</div>
      </div>
      {proposal.ai_note && (
        <div className="rounded-lg p-3 mb-3 text-sm italic" style={{ background: "color-mix(in srgb, var(--accent) 10%, transparent)", color: "var(--text-primary)" }}>“{proposal.ai_note}”</div>
      )}
      <div className="flex gap-2">
        <button onClick={onAccept} disabled={busy || !media} data-testid="sv-proposal-accept" className="flex-1 rounded-xl py-2.5 font-semibold text-sm flex items-center justify-center gap-2" style={{ background: "var(--accent)", color: "var(--bg-base)", opacity: (busy || !media) ? 0.6 : 1 }}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Accept & schedule
        </button>
        <button onClick={onPreview} disabled={busy} data-testid="sv-proposal-preview" className="rounded-xl px-3 py-2.5 font-semibold text-sm flex items-center gap-1.5" style={{ background: "var(--overlay)", color: "var(--text-primary)", border: "1px solid var(--border)" }}><Eye size={15} /> Preview</button>
        <button onClick={onNew} disabled={busy} data-testid="sv-proposal-new" className="rounded-xl px-4 py-2.5 font-semibold text-sm" style={{ background: "var(--overlay)", color: "var(--text-primary)", border: "1px solid var(--border)" }}>New</button>
      </div>
      {!media && <div className="text-[11px] mt-2" style={{ color: "var(--text-muted)" }}>Attach media above to enable scheduling.</div>}
    </div>
  );
}

function GuideMode({ initialMedia, onScheduled }) {
  const [media, setMedia] = useState(initialMedia || null);
  const [mediaSent, setMediaSent] = useState(false);
  const [messages, setMessages] = useState([{ role: "ai", text: initialMedia ? "Hi — I've pulled in your media. Give me a moment to look at it, then I'll help you decide who should receive it and when it should unlock." : "Hi — I'll help you tuck something away for the future. To begin, attach the photo, video, document or audio you'd like to store below." }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [acceptBusy, setAcceptBusy] = useState(false);
  const sessionId = useMemo(() => Math.random().toString(36).slice(2), []);
  const scrollRef = useRef(null);
  const seeded = useRef(false);
  const [preview, setPreview] = useState(null);
  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages, busy]);

  const call = async (message, mediaOverride) => {
    setBusy(true);
    try {
      const m = mediaOverride || media;
      const payload = { session_id: sessionId, message };
      if (!mediaSent && m?.path) { payload.media_path = m.path; payload.media_kind = m.kind; if (m.transcript) payload.media_transcript = m.transcript; setMediaSent(true); }
      const { data } = await api.post("/secret-vault/guide", payload);
      const msg = { role: "ai", text: data.reply || "" };
      if (data.proposal) msg.proposal = data.proposal;
      setMessages((mm) => [...mm, msg]);
    } catch (e) {
      setMessages((mm) => [...mm, { role: "ai", text: formatApiError(e.response?.data?.detail) }]);
    } finally { setBusy(false); }
  };

  // Auto-attach a transferred item (from Legacy / Respawner / Games / Time Capsule).
  useEffect(() => {
    if (!initialMedia || seeded.current) return;
    seeded.current = true;
    setMessages((x) => [...x, { role: "user", text: `📎 Moved in from ${SOURCE_LABEL[initialMedia.source] || "the app"}: ${initialMedia.title}` }]);
    const body = initialMedia.body_md ? `\n\nHere is its content:\n${initialMedia.body_md.slice(0, 4000)}` : "";
    const has = initialMedia.path ? `I've attached my media (a ${initialMedia.kind}). Please take a look and tell me what you see, then ask me about it.` : `I want to store "${initialMedia.title}".`;
    call(`${has}${body}`, initialMedia);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onUploaded = (m) => {
    setMedia(m); setMediaSent(false);
    setMessages((x) => [...x, { role: "user", text: `📎 Attached: ${m.title}` }]);
    call(`I've attached my media (a ${m.kind}). Please take a look and tell me what you see, then ask me about it.`, m);
  };

  const send = (raw) => {
    const q = (raw ?? input).trim();
    if (!q || busy) return;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput("");
    call(q);
  };

  const accept = async (proposal) => {
    if (!media) { toast.error("Attach media first"); return; }
    if (!proposal.recipients?.length) { toast.error("The plan has no recipients yet — ask the concierge to confirm who it's for."); return; }
    setAcceptBusy(true);
    try {
      await api.post("/secret-vault/items", {
        title: proposal.title || media.title, source: media.source || "upload",
        media_kind: media.kind, storage_path: media.path || null,
        ref_id: media.ref_id || null, ref_collection: media.ref_collection || null,
        body_md: media.body_md || null,
        note: proposal.ai_note, ai_note: proposal.ai_note, recipients: proposal.recipients,
        per_recipient: proposal.per_recipient || "individual",
        group_threshold: proposal.group_threshold ?? null, rule: proposal.rule,
      });
      toast.success("Scheduled in your Secret Vault");
      onScheduled();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAcceptBusy(false); }
  };

  return (
    <div className="flex flex-col h-full" data-testid="sv-guide-mode">
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto no-scrollbar space-y-3 pb-2">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "ai" && <div className="h-7 w-7 rounded-full grid place-items-center mr-2 shrink-0 self-start mt-0.5" style={{ background: "color-mix(in srgb, var(--accent) 16%, transparent)" }}><Sparkles size={14} className="gold-text" /></div>}
            <div className="max-w-[85%]">
              {m.text && (
                <div className="rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap" style={m.role === "user" ? { background: "var(--accent)", color: "#fff" } : { background: "var(--overlay)", border: "1px solid var(--border)", color: "var(--text-primary)" }}>{m.text}</div>
              )}
              {m.proposal && <ProposalCard proposal={m.proposal} media={media} busy={acceptBusy} onAccept={() => accept(m.proposal)} onPreview={() => setPreview(m.proposal)} onNew={() => send("Please propose a different plan.")} />}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start"><div className="h-7 w-7 rounded-full grid place-items-center mr-2 shrink-0" style={{ background: "color-mix(in srgb, var(--accent) 16%, transparent)" }}><Sparkles size={14} className="gold-text" /></div>
            <div className="rounded-2xl px-4 py-3 flex items-center gap-1.5" style={{ background: "var(--overlay)", border: "1px solid var(--border)" }}>
              <span className="typing-dot" /><span className="typing-dot" style={{ animationDelay: "0.15s" }} /><span className="typing-dot" style={{ animationDelay: "0.3s" }} />
            </div>
          </div>
        )}
      </div>

      <div className="shrink-0 pt-2">
        {media && <MediaBadge media={media} />}
        {!media ? (
          <MediaDrop onUploaded={onUploaded} compact />
        ) : (
          <div className="flex items-end gap-2">
            <textarea value={input} rows={1} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Reply to the concierge…" data-testid="sv-guide-input" className="flex-1 rounded-2xl px-4 py-3 text-sm outline-none resize-none" style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)", fontSize: 16, maxHeight: "6.5rem" }} />
            <button onClick={() => send()} disabled={busy} data-testid="sv-guide-send" className="h-11 w-11 rounded-xl grid place-items-center shrink-0" style={{ background: "var(--accent)", color: "#fff", opacity: busy ? 0.6 : 1 }}>{busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}</button>
          </div>
        )}
      </div>
      {preview && <DeliveryPreview media={media} title={preview.title} note={preview.ai_note} per_recipient={preview.per_recipient || "individual"} group_threshold={preview.group_threshold} recipients={preview.recipients || []} rule={preview.rule} onClose={() => setPreview(null)} />}
    </div>
  );
}

const SOURCE_LABEL = { lexarra: "the Legacy Suite", respawner: "The Respawner", game: "a family game", timecapsule: "the Time Capsule" };

function ruleToState(r) {
  r = r || {};
  return {
    order: r.order === "in_order" ? "in_order" : "any",
    logic: r.logic || "ALL",
    minCount: r.min_count || 2,
    delayN: r.delay?.n ? String(r.delay.n) : "",
    delayUnit: r.delay?.unit || "days",
    conditions: (r.conditions || []).map((c) => ({ metric: c.metric, op: c.op || ">=", value: c.value ?? "", unit: c.unit, offset: c.offset })),
  };
}

function ManualMode({ recipients, triggers, pickers, initialMedia, editItem, onScheduled, onCancelEdit }) {
  const seed = editItem ? ruleToState(editItem.rule) : null;
  const editMedia = editItem ? { path: editItem.storage_path, kind: editItem.media_kind, title: editItem.title, source: editItem.source, ref_id: editItem.ref_id, ref_collection: editItem.ref_collection, body_md: editItem.body_md } : null;
  const [media, setMedia] = useState(editMedia || initialMedia || null);
  const [selected, setSelected] = useState(editItem?.recipients || []);
  const [title, setTitle] = useState(editItem?.title || initialMedia?.title || "");
  const [note, setNote] = useState(editItem?.note || "");
  const [logic, setLogic] = useState(seed?.logic || "ALL");
  const [order, setOrder] = useState(seed?.order || "any"); // any | in_order
  const [minCount, setMinCount] = useState(seed?.minCount || 2);
  const [conditions, setConditions] = useState(seed?.conditions || []);
  const [delayN, setDelayN] = useState(seed?.delayN || "");
  const [delayUnit, setDelayUnit] = useState(seed?.delayUnit || "days");
  const [perRecipient, setPerRecipient] = useState(editItem?.per_recipient || "individual");
  const [groupThreshold, setGroupThreshold] = useState(editItem?.group_threshold != null ? String(editItem.group_threshold) : "");
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const firstMetric = triggers[0]?.metric || "on_date";
  useEffect(() => { if (triggers.length && conditions.length === 0) setConditions([{ metric: firstMetric, op: ">=", value: "", unit: triggers[0]?.unit === "period" ? "years" : undefined }]); }, [triggers]); // eslint-disable-line

  const groupMode = GROUP_MODES.find((g) => g.v === perRecipient);
  const needsThreshold = !!groupMode?.threshold;

  const buildRule = () => ({
    logic: order === "in_order" ? "ALL" : logic,
    ...(order === "in_order" ? { order: "in_order" } : (logic === "X_OF_Y" ? { min_count: Number(minCount) } : {})),
    ...(delayN ? { delay: { n: Number(delayN), unit: delayUnit } } : {}),
    conditions: conditions.map((c) => ({ metric: c.metric, op: c.op, value: c.value, ...(c.unit ? { unit: c.unit } : {}), ...(c.offset ? { offset: c.offset } : {}) })),
  });

  const save = async () => {
    if (!media?.path && !media?.body_md) { toast.error("Attach media first"); return; }
    if (!selected.length) { toast.error("Pick at least one recipient"); return; }
    if (!title.trim()) { toast.error("Give it a title"); return; }
    if (needsThreshold && !groupThreshold) { toast.error(groupMode.threshold === "percent" ? "Enter a percentage" : "Enter how many recipients"); return; }
    const rule = buildRule();
    const payload = {
      title: title.trim(), source: media.source || "upload", media_kind: media.kind,
      storage_path: media.path || null, ref_id: media.ref_id || null,
      ref_collection: media.ref_collection || null, body_md: media.body_md || null,
      note: note.trim() || null, ai_note: editItem?.ai_note || null,
      recipients: selected, per_recipient: perRecipient,
      group_threshold: needsThreshold ? Number(groupThreshold) : null, rule,
    };
    setBusy(true);
    try {
      if (editItem) {
        await api.put(`/secret-vault/items/${editItem.id}`, payload);
        toast.success("Schedule updated");
      } else {
        await api.post("/secret-vault/items", payload);
        toast.success("Scheduled in your Secret Vault");
      }
      onScheduled();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openPreview = () => {
    if (!selected.length) { toast.error("Pick at least one recipient to preview"); return; }
    setPreview(true);
  };

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= conditions.length) return;
    setConditions((cs) => { const a = [...cs]; [a[i], a[j]] = [a[j], a[i]]; return a; });
  };

  const inp = { background: "var(--overlay)", border: "1px solid var(--border)", color: "var(--text-primary)" };
  return (
    <div className="h-full overflow-y-auto no-scrollbar space-y-4 pb-4" data-testid="sv-manual-mode">
      {editItem && (
        <div className="flex items-center justify-between rounded-xl px-3 py-2" style={{ background: "color-mix(in srgb, var(--accent) 12%, transparent)", border: "1px solid var(--accent)" }} data-testid="sv-edit-banner">
          <span className="text-xs font-semibold gold-text flex items-center gap-1.5"><Pencil size={13} /> Editing a scheduled item</span>
          {onCancelEdit && <button onClick={onCancelEdit} data-testid="sv-cancel-edit" className="text-xs" style={{ color: "var(--text-secondary)" }}>Cancel</button>}
        </div>
      )}
      {media ? <MediaBadge media={media} onClear={(initialMedia || editItem) ? undefined : () => setMedia(null)} /> : <MediaDrop onUploaded={setMedia} />}
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (e.g. For Sarah's 18th)" data-testid="sv-manual-title" className="w-full rounded-xl px-3.5 py-3 text-sm" style={inp} />
      <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="A note to accompany the media (optional)" rows={2} data-testid="sv-manual-note" className="w-full rounded-xl px-3.5 py-3 text-sm resize-none" style={inp} />

      <div>
        <div className="text-xs font-semibold mb-1.5" style={{ color: "var(--text-secondary)" }}>Recipients</div>
        <RecipientSelect recipients={recipients} selected={selected} setSelected={setSelected} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
          <span className="text-xs font-semibold flex items-center gap-1.5" style={{ color: "var(--text-secondary)" }}><Clock size={13} /> Release when</span>
          <div className="flex items-center gap-1.5">
            {order === "any" && (
              <div className="flex gap-1">
                {["ALL", "ANY", "X_OF_Y"].map((l) => (
                  <button key={l} onClick={() => setLogic(l)} data-testid={`sv-logic-${l}`} className="px-2 py-1 rounded-md text-[11px]" style={logic === l ? { background: "var(--accent)", color: "var(--bg-base)" } : { background: "var(--overlay)", color: "var(--text-secondary)" }}>{l === "ALL" ? "All" : l === "ANY" ? "Any" : "X of Y"}</button>
                ))}
              </div>
            )}
            <div className="flex gap-1 pl-1" style={{ borderLeft: "1px solid var(--border)" }}>
              {[["any", "Any order"], ["in_order", "In order"]].map(([v, l]) => (
                <button key={v} onClick={() => setOrder(v)} data-testid={`sv-order-${v}`} className="px-2 py-1 rounded-md text-[11px]" style={order === v ? { background: "var(--accent)", color: "var(--bg-base)" } : { background: "var(--overlay)", color: "var(--text-secondary)" }}>{l}</button>
              ))}
            </div>
          </div>
        </div>
        {order === "in_order" && <div className="text-[11px] mb-2" style={{ color: "var(--text-muted)" }}>Use the arrows to set the order — each unlocks only after the one above it has happened.</div>}
        {order === "any" && logic === "X_OF_Y" && (
          <div className="flex items-center gap-2 mb-2 text-sm" style={{ color: "var(--text-secondary)" }}>
            At least <input type="number" min="1" value={minCount} onChange={(e) => setMinCount(e.target.value)} data-testid="sv-min-count" className="w-16 rounded-lg px-2 py-1.5" style={inp} /> of these
          </div>
        )}
        <div className="space-y-2">
          {conditions.map((c, i) => (
            <div key={i} className="flex items-start gap-2" data-testid={`sv-cond-wrap-${i}`}>
              {order === "in_order" && (
                <div className="flex flex-col items-center gap-0.5 pt-1.5 shrink-0" data-testid={`sv-cond-order-${i}`}>
                  <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`sv-cond-up-${i}`} className="h-6 w-6 rounded-md grid place-items-center disabled:opacity-30" style={{ background: "var(--overlay)", border: "1px solid var(--border)", color: "var(--text-secondary)" }}><ArrowUp size={13} /></button>
                  <span className="text-xs font-bold gold-text">{i + 1}</span>
                  <button onClick={() => move(i, 1)} disabled={i === conditions.length - 1} data-testid={`sv-cond-down-${i}`} className="h-6 w-6 rounded-md grid place-items-center disabled:opacity-30" style={{ background: "var(--overlay)", border: "1px solid var(--border)", color: "var(--text-secondary)" }}><ArrowDown size={13} /></button>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <ConditionRow cond={c} triggers={triggers} pickers={pickers} onChange={(nc) => setConditions((cs) => cs.map((x, j) => j === i ? nc : x))} onRemove={() => setConditions((cs) => cs.filter((_, j) => j !== i))} />
              </div>
            </div>
          ))}
        </div>
        <button onClick={() => setConditions((cs) => [...cs, { metric: firstMetric, op: ">=", value: "", unit: triggers[0]?.unit === "period" ? "years" : undefined }])} data-testid="sv-add-condition" className="mt-2 flex items-center gap-1.5 text-sm gold-text"><Plus size={15} /> Add condition</button>
      </div>

      <div className="flex items-center gap-2 text-sm" style={{ color: "var(--text-secondary)" }}>
        Then wait <input type="number" min="0" value={delayN} onChange={(e) => setDelayN(e.target.value)} placeholder="0" data-testid="sv-delay-n" className="w-16 rounded-lg px-2 py-1.5" style={inp} />
        <select value={delayUnit} onChange={(e) => setDelayUnit(e.target.value)} data-testid="sv-delay-unit" className="rounded-lg px-2 py-1.5" style={inp}>{["days", "weeks", "months", "years"].map((u) => <option key={u} value={u}>{u}</option>)}</select>
      </div>

      <div>
        <div className="text-xs font-semibold mb-1.5 flex items-center gap-1.5" style={{ color: "var(--text-secondary)" }}><Users size={13} /> For multiple recipients, release…</div>
        <select value={perRecipient} onChange={(e) => setPerRecipient(e.target.value)} data-testid="sv-group-mode" className="w-full rounded-xl px-3 py-2.5 text-sm" style={inp}>
          {GROUP_MODES.map((g) => <option key={g.v} value={g.v}>{g.l}</option>)}
        </select>
        {needsThreshold && (
          <div className="flex items-center gap-2 mt-2 text-sm" style={{ color: "var(--text-secondary)" }}>
            {groupMode.threshold === "percent" ? "Percentage of recipients" : "Number of recipients"}
            <input type="number" min="1" max={groupMode.threshold === "percent" ? 100 : undefined} value={groupThreshold} onChange={(e) => setGroupThreshold(e.target.value)} placeholder={groupMode.threshold === "percent" ? "%" : "N"} data-testid="sv-group-threshold" className="w-20 rounded-lg px-2 py-1.5" style={inp} />
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <button onClick={openPreview} data-testid="sv-manual-preview" className="rounded-xl px-4 py-3.5 font-semibold text-sm flex items-center justify-center gap-2" style={{ background: "var(--overlay)", color: "var(--text-primary)", border: "1px solid var(--border)" }}><Eye size={16} /> Preview</button>
        <button onClick={save} disabled={busy} data-testid="sv-manual-save" className="flex-1 rounded-xl py-3.5 font-semibold text-sm flex items-center justify-center gap-2" style={{ background: "var(--accent)", color: "var(--bg-base)" }}>{busy ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />} {editItem ? "Save changes" : "Schedule in Secret Vault"}</button>
      </div>
      {preview && <DeliveryPreview media={media} title={title.trim() || media?.title} note={note.trim()} per_recipient={perRecipient} group_threshold={needsThreshold ? Number(groupThreshold) : null} recipients={selected} rule={buildRule()} onClose={() => setPreview(false)} />}
    </div>
  );
}

// Full detail of a scheduled item — exactly what the recipient will see — with edit/cancel actions.
function ScheduledDetail({ item, onEdit, onCancelled, onClose }) {
  const media = { path: item.storage_path, kind: item.media_kind, title: item.title, body_md: item.body_md };
  const [deleting, setDeleting] = useState(false);
  const cancel = async () => {
    setDeleting(true);
    try { await api.delete(`/secret-vault/items/${item.id}`); toast.success("Schedule cancelled"); onCancelled(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setDeleting(false); }
  };
  return (
    <DeliveryPreview
      media={media} title={item.title} note={item.note || item.ai_note}
      per_recipient={item.per_recipient || "individual"} group_threshold={item.group_threshold}
      recipients={item.recipients || []} rule={item.rule} onClose={onClose}
      heading="Scheduled item"
      actions={(
        <div className="flex gap-2 pt-1">
          <button onClick={() => onEdit(item)} data-testid="sv-detail-edit" className="flex-1 rounded-xl py-2.5 font-semibold text-sm flex items-center justify-center gap-1.5" style={{ background: "var(--accent)", color: "var(--bg-base)" }}><Pencil size={15} /> Modify</button>
          <button onClick={cancel} disabled={deleting} data-testid="sv-detail-cancel" className="rounded-xl px-4 py-2.5 font-semibold text-sm flex items-center gap-1.5" style={{ background: "color-mix(in srgb, #d33 14%, transparent)", color: "#e66", border: "1px solid color-mix(in srgb, #d33 40%, transparent)" }}>{deleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} Cancel</button>
          <button onClick={onClose} data-testid="sv-detail-close-btn" className="rounded-xl px-4 py-2.5 font-semibold text-sm" style={{ background: "var(--overlay)", color: "var(--text-primary)", border: "1px solid var(--border)" }}>Leave</button>
        </div>
      )}
    />
  );
}

function ScheduledList({ items, reload, onEdit }) {
  const [detail, setDetail] = useState(null);
  return (
    <div className="h-full overflow-y-auto no-scrollbar space-y-2" data-testid="sv-scheduled-list">
      {items.length === 0 && <div className="text-center text-sm py-12" style={{ color: "var(--text-muted)" }}>Nothing scheduled yet.</div>}
      {items.map((it) => {
        const Icon = kindIcon(it.media_kind);
        return (
          <button key={it.id} onClick={() => setDetail(it)} className="w-full text-left rounded-xl p-3 flex items-center gap-3" style={{ background: "var(--overlay)" }} data-testid="sv-scheduled-item">
            <div className="h-10 w-10 rounded-lg grid place-items-center shrink-0" style={{ background: "color-mix(in srgb, var(--accent) 14%, transparent)" }}><Icon size={18} className="gold-text" /></div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" style={{ color: "var(--text-primary)" }}>{it.title}</div>
              <div className="text-[11px] truncate" style={{ color: "var(--text-muted)" }}>{it.conditions_summary}</div>
              <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>To {(it.recipient_names || []).join(", ")} · {(it.delivered_to || []).length}/{(it.recipients || []).length} delivered</div>
            </div>
            <Eye size={16} style={{ color: "var(--text-muted)" }} />
          </button>
        );
      })}
      {detail && <ScheduledDetail item={detail} onEdit={(it) => { setDetail(null); onEdit(it); }} onCancelled={() => { setDetail(null); reload(); }} onClose={() => setDetail(null)} />}
    </div>
  );
}

// A single stored document/media viewer with a CTA to schedule its release.
function LibraryDetail({ entry, onClose }) {
  const url = entry.storage_path ? fileUrl(entry.storage_path) : null;
  const kind = entry.kind;
  const [previewOk, setPreviewOk] = useState(null); // null=checking, true/false=result
  useEffect(() => {
    if (!url) { setPreviewOk(false); return; }
    let alive = true;
    fetch(url, { method: "GET" })
      .then((r) => { if (alive) setPreviewOk(r.ok && !(r.headers.get("content-type") || "").includes("application/json")); })
      .catch(() => { if (alive) setPreviewOk(false); });
    return () => { alive = false; };
  }, [url]);
  const showMedia = url && previewOk === true;
  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center" style={{ background: "rgba(0,0,0,0.6)" }} onClick={onClose} data-testid="sv-library-detail">
      <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl p-5 max-h-[92dvh] overflow-y-auto no-scrollbar" style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)" }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 gold-text"><Lock size={16} /><span className="text-[10px] uppercase tracking-widest">Stored in Secret Vault</span></div>
          <button onClick={onClose} data-testid="sv-library-detail-close"><X size={18} style={{ color: "var(--text-secondary)" }} /></button>
        </div>
        <div className="font-serif-display text-xl mb-3" style={{ color: "var(--text-primary)" }}>{entry.title}</div>
        <div className="rounded-xl overflow-hidden mb-3" style={{ background: "var(--overlay)", border: "1px solid var(--border)" }}>
          {showMedia && kind === "image" ? <img src={url} alt={entry.title} className="w-full max-h-[52dvh] object-contain" />
            : showMedia && kind === "video" ? <video src={url} controls className="w-full max-h-[52dvh]" />
            : showMedia && kind === "audio" ? <audio src={url} controls className="w-full p-3" />
            : showMedia && kind === "pdf" ? <iframe title={entry.title} src={url} className="w-full" style={{ height: "52dvh", border: "none" }} data-testid="sv-library-pdf" />
            : previewOk === null && url ? <div className="flex items-center gap-2 p-4 text-sm" style={{ color: "var(--text-muted)" }}><Loader2 size={16} className="animate-spin" /> Loading preview…</div>
            : url && previewOk === false ? <div className="flex items-center gap-2 p-4 text-sm" style={{ color: "var(--text-muted)" }}><FileText size={16} /> Preview unavailable · {entry.title}</div>
            : <div className="flex items-center gap-2 p-4 text-sm gold-text"><FileText size={16} /> {entry.title}</div>}
        </div>
        {entry.body_md && <div className="vault-md rounded-lg p-3 mb-3 text-sm" style={{ background: "var(--overlay)", color: "var(--text-primary)" }}><ReactMarkdown remarkPlugins={[remarkGfm]}>{entry.body_md}</ReactMarkdown></div>}
        <MoveToVaultButton
          testid="sv-library-schedule"
          label="Schedule a release"
          className="w-full flex items-center justify-center gap-2 rounded-xl py-3 font-semibold text-sm"
          media={{ path: entry.storage_path || null, kind: entry.kind, title: entry.title, source: entry.source || "lexarra", ref_id: entry.id, ref_collection: "secret_vault_items", body_md: entry.body_md || null }} />
      </div>
    </div>, document.body);
}

function LibraryList() {
  const [items, setItems] = useState(null);
  const [detail, setDetail] = useState(null);
  useEffect(() => { api.get("/secret-vault/library").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  return (
    <div className="h-full overflow-y-auto no-scrollbar space-y-2" data-testid="sv-library-list">
      <p className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>Documents &amp; media moved into your Secret Vault. Open one to view it and schedule its release.</p>
      {items === null && <div className="grid place-items-center py-12"><Loader2 className="animate-spin gold-text" /></div>}
      {items && items.length === 0 && <div className="text-center text-sm py-12" style={{ color: "var(--text-muted)" }}>Nothing stored yet. Move a document here from the Legacy Suite or another vault.</div>}
      {items && items.map((it) => {
        const Icon = kindIcon(it.kind);
        return (
          <button key={it.id} onClick={() => setDetail(it)} className="w-full text-left rounded-xl p-3 flex items-center gap-3" style={{ background: "var(--overlay)" }} data-testid="sv-library-item">
            <div className="h-10 w-10 rounded-lg grid place-items-center shrink-0" style={{ background: "color-mix(in srgb, var(--accent) 14%, transparent)" }}><Icon size={18} className="gold-text" /></div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" style={{ color: "var(--text-primary)" }}>{it.title}</div>
              <div className="text-[11px] truncate" style={{ color: "var(--text-muted)" }}>{it.source === "lexarra" ? "From Legacy Suite" : it.source}{it.doc_type ? ` · ${it.doc_type}` : ""}</div>
            </div>
            <Eye size={16} style={{ color: "var(--text-muted)" }} />
          </button>
        );
      })}
      {detail && <LibraryDetail entry={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

export function SecretVault({ scheduledView = false, libraryView = false }) {
  const [mode, setMode] = useState("guide"); // guide | manual
  const [recipients, setRecipients] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [pickers, setPickers] = useState({ reunions: [], people: [], items: [] });
  const [items, setItems] = useState([]);
  const [transfer, setTransfer] = useState(null);
  const [editItem, setEditItem] = useState(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("fv_sv_transfer");
      if (raw) {
        const t = JSON.parse(raw);
        setTransfer(t);
        if (t.mode === "manual" || t.mode === "guide") setMode(t.mode);
        localStorage.removeItem("fv_sv_transfer");
      }
    } catch { /* noop */ }
  }, []);

  const load = async () => {
    try {
      const [r, t, it, p] = await Promise.all([
        api.get("/secret-vault/recipients"), api.get("/secret-vault/triggers"),
        api.get("/secret-vault/items"), api.get("/secret-vault/pickers"),
      ]);
      setRecipients(r.data); setTriggers(t.data.triggers); setItems(it.data); setPickers(p.data);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);
  const onScheduled = () => { setEditItem(null); load(); };

  // Editing a scheduled item takes over the builder even while the list is showing.
  if (editItem) {
    return (
      <div className="h-full" data-testid="secret-vault">
        <ManualMode key={`edit-${editItem.id}`} recipients={recipients} triggers={triggers} pickers={pickers} editItem={editItem} onScheduled={onScheduled} onCancelEdit={() => setEditItem(null)} />
      </div>
    );
  }
  if (libraryView) return <div className="h-full" data-testid="secret-vault"><LibraryList /></div>;
  if (scheduledView) return <div className="h-full" data-testid="secret-vault"><ScheduledList items={items} reload={load} onEdit={(it) => setEditItem(it)} /></div>;

  const media = transfer?.media || null;

  return (
    <div className="flex flex-col h-full" data-testid="secret-vault">
      <div className="flex gap-1 mb-4 p-1 rounded-xl shrink-0" style={{ background: "var(--overlay)" }}>
        {[["guide", "Guide me", Sparkles], ["manual", "Manual", SlidersHorizontal]].map(([k, l, Icon]) => (
          <button key={k} onClick={() => setMode(k)} data-testid={`sv-mode-${k}`} className="flex-1 rounded-lg py-2 text-sm font-semibold flex items-center justify-center gap-1.5" style={mode === k ? { background: "var(--accent)", color: "var(--bg-base)" } : { color: "var(--text-secondary)" }}>
            <Icon size={15} /> {l}
          </button>
        ))}
      </div>
      <div className="flex-1 min-h-0">
        {mode === "guide"
          ? <GuideMode key={media?.path || media?.ref_id || media?.body_md?.slice(0, 24) || "fresh"} initialMedia={media} onScheduled={onScheduled} />
          : <ManualMode recipients={recipients} triggers={triggers} pickers={pickers} initialMedia={media} onScheduled={onScheduled} />}
      </div>
    </div>
  );
}
